import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  ArrowLeft, 
  RefreshCw, 
  Eye,
  Settings,
  AlertCircle,
  Download,
  ExternalLink,
  RotateCcw,
  PlayCircle,
  Pause
} from 'lucide-react';
import { Job, jobService } from '../services/job';
import { useToast } from '../hooks/use-toast';

export const JobDetailsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryingUpload, setRetryingUpload] = useState(false);
  
  // Auto-refresh state
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(Date.now());

  const jobId = searchParams.get('job_id');

  const fetchJob = async () => {
    if (!jobId) {
      setError('No job ID provided');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await jobService.getJob(jobId);
      
      if (response.success) {
        setJob(response.data);
        setError(null);
        setLastRefresh(Date.now());
      } else {
        throw new Error('Failed to fetch job');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch job details';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Auto-refresh functionality
  useEffect(() => {
    if (!autoRefresh || !job) return;
    
    // Only auto-refresh if job is in a non-terminal state
    const isActiveJob = job && ['pending', 'processing', 'uploading_to_magento'].includes(job.status);
    if (!isActiveJob) return;
    
    const interval = setInterval(() => {
      fetchJob();
    }, 5000); // Refresh every 5 seconds
    
    return () => clearInterval(interval);
  }, [autoRefresh, job?.status, jobId]);

  useEffect(() => {
    fetchJob();
  }, [jobId]);

  const handleBack = () => {
    navigate('/jobs');
  };

  const handleRefresh = () => {
    fetchJob();
  };

  const handleRetryUpload = async () => {
    if (!job) return;
    
    try {
      setRetryingUpload(true);
      const response = await jobService.retryMagentoUpload(job.id);
      
      if (response.success) {
        toast({
          title: 'Retry Initiated',
          description: 'Magento upload retry has been started. The job status will update shortly.',
        });
        
        // Refresh the job data to show updated status
        setTimeout(() => {
          fetchJob();
        }, 1000);
      } else {
        throw new Error('Failed to initiate retry');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to retry upload';
      toast({
        title: 'Retry Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setRetryingUpload(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-950/50';
      case 'processing': return 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-950/50';
      case 'processed_pending_approval': return 'text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-950/50';
      case 'approved': return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-950/50';
      case 'uploading_to_magento': return 'text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-950/50';
      case 'complete': return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-950/50';
      case 'failed': return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-950/50';
      case 'cancelled': return 'text-muted-foreground bg-muted/50';
      default: return 'text-muted-foreground bg-muted/50';
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground mr-4" />
          <span className="text-foreground">Loading job details...</span>
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="container mx-auto px-4 py-6 space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Jobs
          </Button>
          <h1 className="text-2xl font-bold">Job Details</h1>
        </div>
        
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error || 'Job not found'}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Back Button */}
      <div>
        <Button variant="outline" onClick={handleBack} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Jobs
        </Button>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Job Details</h1>
          <p className="text-muted-foreground">View detailed information about this job</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Retry button for failed jobs */}
          {job.status === 'failed' && job.error_message && (
            <Button 
              variant="outline" 
              onClick={handleRetryUpload}
              disabled={retryingUpload}
              className="bg-orange-50 hover:bg-orange-100 border-orange-200 text-orange-700 dark:bg-orange-950/50 dark:hover:bg-orange-950/70 dark:border-orange-800 dark:text-orange-400"
            >
              {retryingUpload ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Retrying...
                </>
              ) : (
                <>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Retry Upload
                </>
              )}
            </Button>
          )}
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={autoRefresh ? 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300' : ''}
          >
            {autoRefresh ? <Pause className="w-4 h-4 mr-1" /> : <PlayCircle className="w-4 h-4 mr-1" />}
            {autoRefresh ? 'Auto' : 'Manual'}
          </Button>
          <Button variant="outline" onClick={handleRefresh}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Job Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Job Overview
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {autoRefresh && ['pending', 'processing', 'uploading_to_magento'].includes(job.status) && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-green-50 dark:bg-green-950/20 p-2 rounded-md">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                Auto-updating every 5 seconds
              </div>
              <div>
                Last updated: {new Date(lastRefresh).toLocaleTimeString()}
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Job ID</label>
              <p className="font-mono text-sm">{job.id}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Status</label>
              <div className="mt-1">
                <Badge className={getStatusColor(job.status)}>
                  {job.status.replace(/_/g, ' ').toUpperCase()}
                </Badge>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Product</label>
              <p>{job.products?.name || 'Unknown Product'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Product SKU</label>
              <p className="font-mono text-sm">{job.products?.sku || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Created</label>
              <p>{new Date(job.created_at).toLocaleString()}</p>
            </div>
            {job.processed_at && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Processed</label>
                <p>{new Date(job.processed_at).toLocaleString()}</p>
              </div>
            )}
            {job.approved_at && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Approved</label>
                <p>{new Date(job.approved_at).toLocaleString()}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Images - Main Focus */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <Eye className="h-5 w-5" />
            Image Comparison
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {job.original_image_url && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-foreground">Original Image</h3>
                  <a
                    href={job.original_image_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-1 bg-muted hover:bg-muted/80 rounded-lg transition-colors text-sm"
                  >
                    <ExternalLink className="h-4 w-4" />
                    View Full Size
                  </a>
                </div>
                <div className="relative bg-muted/20 rounded-lg overflow-hidden">
                  <img
                    src={job.original_image_url}
                    alt="Original"
                    className="w-full h-auto max-h-[600px] object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/placeholder-image.png';
                    }}
                  />
                </div>
              </div>
            )}
            
            {job.processed_image_url && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-foreground">Processed Image</h3>
                  <div className="flex items-center gap-2">
                    <a
                      href={job.processed_image_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 py-1 bg-blue-100 dark:bg-blue-950/50 hover:bg-blue-200 dark:hover:bg-blue-950/70 rounded-lg transition-colors text-sm"
                    >
                      <ExternalLink className="h-4 w-4" />
                      View Full Size
                    </a>
                    <a
                      href={job.processed_image_url}
                      download
                      className="flex items-center gap-2 px-3 py-1 bg-green-100 dark:bg-green-950/50 hover:bg-green-200 dark:hover:bg-green-950/70 rounded-lg transition-colors text-sm"
                    >
                      <Download className="h-4 w-4" />
                      Download
                    </a>
                  </div>
                </div>
                <div className="relative bg-muted/20 rounded-lg overflow-hidden">
                  <img
                    src={job.processed_image_url}
                    alt="Processed"
                    className="w-full h-auto max-h-[600px] object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/placeholder-image.png';
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Single Image Layout - When only one image exists */}
          {job.original_image_url && !job.processed_image_url && (
            <div className="max-w-4xl mx-auto">
              <div className="text-center text-muted-foreground mb-4">
                <p>Processing not yet completed - only original image available</p>
              </div>
            </div>
          )}
          
          {!job.original_image_url && job.processed_image_url && (
            <div className="max-w-4xl mx-auto">
              <div className="text-center text-muted-foreground mb-4">
                <p>Original image not available - showing processed result only</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transformations */}
      {job.transformations && job.transformations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Transformations Applied
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {job.transformations.map((transformation, index) => (
                <div key={index} className="p-3 bg-muted/50 rounded-lg">
                  <div className="font-medium text-foreground">{transformation.type.replace(/_/g, ' ').toUpperCase()}</div>
                  <div className="text-sm text-muted-foreground mt-1">
                    {JSON.stringify(transformation, null, 2)}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Error Details */}
      {job.error_message && (
        <Card>
          <CardHeader>
            <CardTitle className="text-red-600 dark:text-red-400 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Error Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-red-800 dark:text-red-200 text-sm">{job.error_message}</p>
            </div>
            
            {/* Retry section for failed jobs */}
            {job.status === 'failed' && (
              <div className="p-4 bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-orange-800 dark:text-orange-200">Upload Failed</h4>
                    <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
                      The job failed to upload to Magento. You can retry the upload process.
                    </p>
                    {job.retry_count > 0 && (
                      <p className="text-xs text-orange-600 dark:text-orange-400 mt-2">
                        Retry attempts: {job.retry_count}/{job.max_retries}
                      </p>
                    )}
                  </div>
                  <Button 
                    onClick={handleRetryUpload}
                    disabled={retryingUpload || job.retry_count >= job.max_retries}
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    {retryingUpload ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Retrying...
                      </>
                    ) : job.retry_count >= job.max_retries ? (
                      'Max Retries Reached'
                    ) : (
                      <>
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Retry Upload
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default JobDetailsPage;